import React, { useMemo } from 'react'
import useSWR from 'swr'
import useSWRMutation from 'swr/mutation'
import { Link } from 'react-router-dom'

import Translate from '../../../libraries/i18n/Translate'
import DescriptionText from '../../components/content/DescriptionText'
import FileUpload from '../../components/form/FileUpload'
import { useI18N } from '../../../libraries/i18n/hook'
import FormGroup from '../../components/form/FormGroup'
import useForm from '../../../libraries/form/hook'
import { ApplicationFieldTypes, UserAcademicTranscript, type ApplicationField, type JobOfferApplication, type JobOfferApplicationLanguageLevel } from '../../../api/job-offers/types'
import { applyJobOffer } from '../../../api/job-offers'
import { getUserLanguages } from '../../../api/users'
import Button from '../../components/form/Button'
import ErrorMessage from '../../components/errors/ErrorMessage'
import TextArea from '../../components/form/TextArea'
import ArrowTop from '../../../assets/icons/arrow-top-right-on-square.svg?react'
import Expandable from '../../components/layout/Expandable'
import MatchLevelCV from '../../components/job-offers/MatchLevelCV'
import { useCVForOffer } from '../cv/hooks'
import { CVQuality } from '../../../api/cv-roaster/types'
import { useJobOfferApplication } from './hooks'
import Loading from '../../components/layout/Loading'
import PremiumTag from '../../components/premium/Tag'
import FormLabel from '../../components/form/FormLabel'
import RadioButton from '../../components/form/RadioButton'
import SelectInput from '../../components/form/SelectInput'
import type { OptionType } from '../../components/form/SelectInput'

type Props = {
  id: string
  companyName: string
  workShiftLabel?: string
  isInternship?: boolean
  isLoading: boolean
  latestCvUrl?: string
  latestAcademicTranscript?: UserAcademicTranscript
  latestProfessionalContext?: string
  customFields?: ApplicationField[] | null
  countryName: string
  confirmLocation?: boolean
}

const levelOptionsFromApi = (levels: { level: number; label: string }[]): OptionType[] =>
  levels.map(l => ({ value: String(l.level), label: l.label }))

type ApplicationLanguageLevelsSectionProps = {
  languageLevels: JobOfferApplicationLanguageLevel[]
  requiredLanguageCodes: string[]
  languagesOptions: { code: string; name: string }[]
  levelsOptions: { level: number; label: string }[]
  onLevelChange: (code: string, level: number) => void
}

const PLACEHOLDER_LEVEL_VALUE = ''

const ApplicationLanguageLevelsSection: React.FC<ApplicationLanguageLevelsSectionProps> = ({
  languageLevels,
  requiredLanguageCodes,
  languagesOptions,
  levelsOptions,
  onLevelChange,
}) => {
  const { t } = useI18N()
  const levelOptions = useMemo(() => [
    { value: PLACEHOLDER_LEVEL_VALUE, label: t('jobs.apply.languages.selectLevel') },
    ...levelOptionsFromApi(levelsOptions),
  ], [levelsOptions, t])
  const codeToName = useMemo(() => Object.fromEntries(languagesOptions.map(l => [l.code, l.name])), [languagesOptions])

  return (
    <FormGroup>
      <label className='mb-2 text-sm font-medium'>{t('jobs.apply.languages.title')}</label>
      <ul className='space-y-3'>
        {requiredLanguageCodes.map(code => {
          const entry = languageLevels.find(e => e.code === code)
          const level = entry?.level ?? 0
          const currentLevelOption = level > 0
            ? levelOptions.find(o => o.value === String(level))
            : levelOptions[0]
          return (
            <li key={code} className='flex flex-wrap items-center gap-2 border-b border-gray-200 last:border-0 pb-2'>
              <span className='font-semibold text-center min-w-[150px]'>{codeToName[code] ?? code}</span>
              <div className='flex-grow max-w-xs'>
                <SelectInput
                  name={`language-level-${code}`}
                  options={levelOptions}
                  value={currentLevelOption}
                  onChange={v => onLevelChange(code, v === PLACEHOLDER_LEVEL_VALUE ? 0 : Number(v))}
                  isMulti={false}
                  label={t('profile.languages.level.label')}
                  isSearchable={false}
                  size='sm'
                  required
                  showBorder
                />
              </div>
            </li>
          )
        })}
      </ul>
    </FormGroup>
  )
}

const JobOfferApplicationContainer: React.FC<Props> = ({
  id,
  companyName,
  workShiftLabel,
  isInternship,
  latestProfessionalContext,
  isLoading,
  latestCvUrl,
  latestAcademicTranscript,
  customFields,
  countryName,
  confirmLocation,
}) => {
  const [confirmedWorkShift, setConfirmedWorkShift] = React.useState(false)
  const [confirmedLocation, setConfirmedLocation] = React.useState(false)
  const { data: userApplication, isLoading: isLoadingUserApplication, error: errorJobOfferApplication, mutate: fetchAgainApplication } = useJobOfferApplication(parseInt(id))
  const { data: latestCv, isLoading: loadingLatestCV, error: errorLoadingCv } = useCVForOffer(parseInt(id))

  const getCustomField = (name: string) => {
    return customFields?.find(field => field.name === name)
  }
  const academicTranscriptField = getCustomField(ApplicationFieldTypes.ACADEMIC_TRANSCRIPT)
  const coverLetterField = getCustomField(ApplicationFieldTypes.COVER_LETTER)
  const languageLevelField = getCustomField(ApplicationFieldTypes.LANGUAGE_LEVEL)
  const requiredLanguageCodes = useMemo(() => languageLevelField?.values ?? [], [languageLevelField?.values])

  const { data: userLanguagesData, isLoading: isLoadingUserLanguages } = useSWR(
    requiredLanguageCodes.length > 0 ? '/users/languages' : null,
    getUserLanguages
  )
  const initialLanguageLevels = useMemo((): JobOfferApplicationLanguageLevel[] => {
    if (requiredLanguageCodes.length === 0) return []
    const items = userLanguagesData?.items ?? []
    return requiredLanguageCodes.map(code => ({
      code,
      level: items.find((i: { code: string }) => i.code === code)?.level ?? 0,
    }))
  }, [requiredLanguageCodes, userLanguagesData?.items])

  const { fields, error: errorSubmittingApplication, submitting, handleChange, handleSubmit } = useForm<
    Partial<JobOfferApplication>,
    JobOfferApplication,
    '/job-offers/:id/applications'
  >({
    initialValues: {
      professionalContext: latestProfessionalContext,
      cv: null,
      academicTranscript: null,
      academicTranscriptId: academicTranscriptField ? latestAcademicTranscript?.id : undefined,
      coverLetter: null,
      canSignInternshipAgreement: undefined as boolean | undefined,
      jobOfferId: parseInt(id),
      languageLevels: initialLanguageLevels,
    },
    mutation: useSWRMutation(`/job-offers/${id}/applications`, applyJobOffer),
    onSuccess: fetchAgainApplication,
    transformBody: (body) => {
      if (requiredLanguageCodes.length === 0) return body
      const fromFields = body.languageLevels ?? []
      const mergedLevels = requiredLanguageCodes
        .map(code => {
          const fromFieldsEntry = fromFields.find((e: JobOfferApplicationLanguageLevel) => e.code === code)
          const fromInitialEntry = initialLanguageLevels.find(e => e.code === code)
          const level = (fromFieldsEntry?.level != null && fromFieldsEntry.level > 0)
            ? fromFieldsEntry.level
            : (fromInitialEntry?.level ?? 0)
          return { code, level }
        })
        .filter(e => e.level > 0)
      return { ...body, languageLevels: mergedLevels }
    },
  })

  const languageLevelsForDisplay = useMemo((): JobOfferApplicationLanguageLevel[] => {
    if (requiredLanguageCodes.length === 0) return []
    const fromFields = fields?.languageLevels ?? []
    return requiredLanguageCodes.map(code => {
      const fromFieldsEntry = fromFields.find(e => e.code === code)
      const fromInitialEntry = initialLanguageLevels.find(e => e.code === code)
      const level = (fromFieldsEntry?.level != null && fromFieldsEntry.level > 0)
        ? fromFieldsEntry.level
        : (fromInitialEntry?.level ?? 0)
      return { code, level }
    })
  }, [requiredLanguageCodes, fields?.languageLevels, initialLanguageLevels])

  const { t } = useI18N()

  const onCVSelected = (selectedFile: File) => {
    if (selectedFile) {
      // Create object URL for file preview
      handleChange({
        type: 'file',
        target: { type: 'file', name: 'cv', value: selectedFile },
      })
    }
  }

  const onAcademicTranscriptSelected = (selectedFile: File) => {
    if (selectedFile) {
      handleChange({
        type: 'file',
        target: { type: 'file', name: 'academicTranscript', value: selectedFile },
      })
    }
  }

  const onCoverLetterSelected = (selectedFile: File) => {
    if (selectedFile) {
      handleChange({
        type: 'file',
        target: { type: 'file', name: 'coverLetter', value: selectedFile },
      })
    }
  }

  if (isLoadingUserApplication) { return <Loading /> }
  if (requiredLanguageCodes.length > 0 && isLoadingUserLanguages) { return <Loading /> }
  if (errorJobOfferApplication) { return <ErrorMessage message={errorJobOfferApplication.toString()} /> }

  const isApplied = userApplication && userApplication?.jobOfferId === parseInt(id)

  const ApplyForm = (
    <>
      <h2 className='text-black leading-none p-0 '>{t('jobs.apply.offer.now')}</h2>
      <form onSubmit={handleSubmit} className='space-y-6'>
        <FormGroup>
          <div className='space-y-2'>
            <FormLabel label='CV' isRequired />
            <FileUpload
              onFileSelected={onCVSelected}
              label=''
              accept='.pdf'
              defaultButtonLabel={{
                select: t('cv.upload.select'),
                change: t('cv.upload.change'),
              }}
              disabled={isLoading}
              defaultFile={{ url: latestCvUrl, label: t('jobs.apply.latest.cv.used') }}
              maxMbSize={1}
            />
          </div>
        </FormGroup>
          {academicTranscriptField && (
            <FormGroup>
              <div className='space-y-2'>
                <FormLabel label={t('academic.transcript.title')} isRequired={academicTranscriptField.isRequired} />
                <FileUpload
                  onFileSelected={onAcademicTranscriptSelected}
                  label=''
                  accept='.pdf'
                  defaultButtonLabel={{
                    select: t('academic.transcript.upload.select'),
                    change: t('academic.transcript.upload.change'),
                  }}
                  disabled={isLoading}
                  defaultFile={{ url: latestAcademicTranscript?.srcUrl, label: t('academic.transcript.latest.used') }}
                  maxMbSize={1}
                />
              </div>
            </FormGroup>
          )}
          {coverLetterField && (
            <FormGroup>
              <div className='space-y-2'>
                <FormLabel label={t('jobs.apply.coverLetter.title')} isRequired={coverLetterField.isRequired} />
                <FileUpload
                  onFileSelected={onCoverLetterSelected}
                  label=''
                  accept='.pdf'
                  defaultButtonLabel={{
                    select: t('jobs.apply.coverLetter.upload.select'),
                    change: t('jobs.apply.coverLetter.upload.change'),
                  }}
                  disabled={isLoading}
                  maxMbSize={1}
                />
              </div>
            </FormGroup>
          )}

        {requiredLanguageCodes.length > 0 && (
          <ApplicationLanguageLevelsSection
            languageLevels={languageLevelsForDisplay}
            requiredLanguageCodes={requiredLanguageCodes}
            languagesOptions={userLanguagesData?.languages ?? []}
            levelsOptions={userLanguagesData?.levels ?? []}
            onLevelChange={(code, level) => {
              const current = fields?.languageLevels?.length === requiredLanguageCodes.length
                ? (fields.languageLevels ?? [])
                : initialLanguageLevels
              const next = current.map(entry => entry.code === code ? { ...entry, level } : entry)
              handleChange({ target: { name: 'languageLevels', value: next } } as unknown as React.ChangeEvent<HTMLInputElement>)
            }}
          />
        )}

        <FormGroup>
          <div className='space-y-2 mt-3'>
            <FormLabel
              label={t('jobs.apply.profContext.label')}
              isRequired
              helpText={`${fields?.professionalContext?.length || 0}/500 ${t('common.characters')}`}
            />
            <TextArea
              name='professionalContext'
              placeholder={t('jobs.apply.profContext.placeholder')}
              defaultValue={fields?.professionalContext}
              value={fields?.professionalContext}
              onChange={handleChange}
              maxLength={500}
              required
            />
          </div>
        </FormGroup>

        {workShiftLabel && (
          <FormGroup>
            <label className='flex items-start gap-2 cursor-pointer text-sm'>
              <input
                type='checkbox'
                id='confirm-work-shift'
                checked={confirmedWorkShift}
                onChange={(e) => setConfirmedWorkShift(e.target.checked)}
                required
                className='mt-1 shrink-0'
              />
              <span><Translate i18nKey='jobs.apply.confirmWorkShift' values={{ workShiftLabel }} /></span>
            </label>
          </FormGroup>
        )}

        {isInternship && (
          <FormGroup>
            <FormLabel label={t('jobs.apply.canSignInternshipAgreement.label')} isRequired />
            <div className='flex gap-4 flex-wrap mt-1'>
              <RadioButton
                name='canSignInternshipAgreement'
                value='true'
                label={t('common.yes')}
                checked={fields?.canSignInternshipAgreement === true}
                onChange={() => handleChange({ target: { name: 'canSignInternshipAgreement', value: true } } as unknown as React.ChangeEvent<HTMLInputElement>)}
                required={isInternship}
              />
              <RadioButton
                name='canSignInternshipAgreement'
                value='false'
                label={t('common.no')}
                checked={fields?.canSignInternshipAgreement === false}
                onChange={() => handleChange({ target: { name: 'canSignInternshipAgreement', value: false } } as unknown as React.ChangeEvent<HTMLInputElement>)}
                required={isInternship}
              />
            </div>
          </FormGroup>
        )}

        {confirmLocation && (
          <FormGroup>
            <label className='flex items-start gap-2 cursor-pointer text-sm'>
              <input
                type='checkbox'
                id='confirm-location'
                checked={confirmedLocation}
                onChange={(e) => setConfirmedLocation(e.target.checked)}
                required
                className='mt-1 shrink-0'
              />
              <span><Translate i18nKey='jobs.apply.confirmLocation' values={{ countryName }} /></span>
            </label>
          </FormGroup>
        )}


        <div className='text-center mt-6'>
          <Button
            type='submit'
            disabled={
              submitting ||
              (confirmLocation && !confirmedLocation) ||
              (!!workShiftLabel && !confirmedWorkShift) ||
              (!!isInternship && [true, false, 'true', 'false'].indexOf(fields?.canSignInternshipAgreement as string | boolean) === -1) ||
              (requiredLanguageCodes.length > 0 && languageLevelsForDisplay.some(e => !e?.level || e.level === 0))
            }
            size='lg'
          >
            {t('jobs.apply.offer.now')}
          </Button>
        </div>
      </form>

      {errorSubmittingApplication && <ErrorMessage message={errorSubmittingApplication?.toString()} />}
    </>
  )

  const ApplicationSent = (
    <div>
      <h2 className='text-black leading-none p-0 mb-4'>{t('jobs.application.sent')}</h2>
      <div className='p-4 bg-gray-300 rounded-lg text-center space-y-2'>
        <b>{t('jobs.apply.success')}</b>
        <p className='text-xs md:text-sm'>{t('jobs.apply.success.description')}</p>
      </div>
    </div>
  )

  const renderLink = (to: string, label: string) => (
    <Link to={to} replace className='flex items-center gap-x-2 font-normal text-2xs md:text-xs'>
      <ArrowTop /> {label}
    </Link>
  )

  return (
    <div className='space-y-4'>
      <DescriptionText>
        <Translate
          i18nKey='jobs.apply.description'
          values={{ company: companyName }}
        />
      </DescriptionText>

      <div className='flex items-center gap-x-4 pt-6'>
        <h2 className='text-black leading-none p-0'>
          {isApplied ? t('jobs.prepare.for.interview') : t('jobs.improve.candidate.title')}
        </h2>
        <PremiumTag />
      </div>
      <DescriptionText>
        {t('jobs.improve.candidate.description')}
      </DescriptionText>
      {!loadingLatestCV && !errorLoadingCv && latestCv?.analysis && latestCv?.analysis.CV_quality ? <MatchLevelCV cvQuality={latestCv.analysis.CV_quality as CVQuality} /> : null}
      <div className='pt-2 border-t-soft-green border-t-2 border-solid'>
        <Expandable label={t('jobs.improve.candidate.subTitle')} defaultOpen>
          {!isApplied && renderLink(`/cv-matching/job/${id}`, t('jobs.improve.candidate.adapt.cv'))}
          {renderLink(`/ai-interview/custom-interview/job/${id}`, t('jobs.prepare.interview'))}
          {renderLink(`/ai-interview/fit-interview/job/${id}`, t('jobs.prepare.interview.fit'))}
          {renderLink(`/ai-interview/behavioural/job/${id}`, t('jobs.prepare.interview.behavioural'))}
        </Expandable>
      </div>

      {isApplied ? ApplicationSent : ApplyForm}
    </div>
  )
}

export default JobOfferApplicationContainer
